import { SignalIcon as StatusOnlineIcon } from '@heroicons/react/24/outline';
import {
  min as closestFutureDate,
  max as closestPastDate,
  format,
  formatDistanceToNowStrict as formatDistanceToNow,
  isFuture,
  isPast,
  isToday,
  parseISO,
} from 'date-fns';
import { useMemo } from 'react';
import Countdown from '../../../Components/Countdown';
import { FuneralService } from '../../../types';

const Banner = ({ isLive, service, showStreamCountdown }: { isLive: boolean; service?: FuneralService; showStreamCountdown: boolean }) => {
  const { nextStream, mostRecentStream } = useMemo(() => {
    // The display_after property is the start date-time of the stream (in UTC)
    // Events that do not have a stream will have this property set to undefined
    const streamStartTimes = (service?.end_of_stream_messages ?? []).filter(({ display_after }) => display_after);
    if (streamStartTimes.length === 0 || isLive) {
      return {
        nextStream: null,
        mostRecentStream: null,
      };
    }

    // Split the stream start times into past and future
    const { startedStreams, futureStreams } = streamStartTimes.reduce(
      (acc, value) => {
        const streamStartDate = parseISO(value.display_after);
        if (isFuture(streamStartDate)) {
          // if the stream is in the future, we want to display how long until it starts
          acc.futureStreams.push(streamStartDate);
        } else {
          // if the stream has started, we want to display how long ago it ended
          // if the stream is still running we'd never hit here due to the isLive check
          acc.startedStreams.push(parseISO(value.display_ended));
        }
        return acc;
      },
      {
        startedStreams: [] as Date[],
        futureStreams: [] as Date[],
      },
    );

    return {
      nextStream: futureStreams.length > 0 ? closestFutureDate(futureStreams) : null,
      mostRecentStream: startedStreams.length > 0 ? closestPastDate(startedStreams) : null,
    };
  }, [service?.end_of_stream_messages, isLive]);

  if (isLive) return null;

  if (nextStream) {
    return (
      <div className="service-banner-container">
        <div className="service-banner-inner-container">
          <StatusOnlineIcon className="banner-status-icon" />
          <div>
            Stream starts here on <strong>{format(nextStream, 'EEEE MMM do')}</strong> at{' '}
            <strong className="service-banner-bold">{format(nextStream, 'h:mm a')}</strong>
          </div>
        </div>
        {showStreamCountdown ? <Countdown date={nextStream} /> : null}
      </div>
    );
  }

  if (mostRecentStream && isToday(mostRecentStream)) {
    if (isPast(mostRecentStream)) {
      return (
        <div className="service-banner-container">
          <div className="service-banner-inner-container">
            <StatusOnlineIcon className="banner-status-icon" />
            <div>
              Stream finished <strong>{formatDistanceToNow(mostRecentStream, { addSuffix: true })}</strong>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="service-banner-container">
        <div className="service-banner-inner-container">
          <StatusOnlineIcon className="banner-status-icon" />
          <div>Stream starting</div>
        </div>
      </div>
    );
  }
};
export default Banner;
